/**
 * Держатели шеров СЕЙЧАС — второй вопрос панели «Накоплено на рынке».
 *
 * # Чем этот список отличается от таблицы накопления
 *
 * Он про БАЛАНСЫ, а не про сделки. В баланс входят шеры, полученные минтом пары
 * (split), а минт сделкой не является и в историю /trades не попадает вовсе.
 * Замер 2026-08-10: 227 держателей против 770 наторговавших — списки разные, и
 * расхождение по ОДНОМУ кошельку само есть сигнал: значит, он минтил, а не
 * покупал.
 *
 * # Зачем здесь цена и деньги
 *
 * Потому что «держит 4 200 Up» не отвечает на вопрос, который задают дальше:
 * дорого ли ему это встало и сколько это стоит сейчас. Обе величины уже приехали
 * в том же ответе — средняя цена покупок считается из накопления, текущая
 * стоимость из последней цены принта, — и считать их на сервере значило бы
 * завести второе место расчёта того же числа.
 *
 * # Чего список НЕ обещает
 *
 * Полноты. Источник отдаёт ТОП по балансу с потолком на токен (200 строк), и
 * насыщение выдачи видно по полю `rows` группы. Отсутствие кошелька в списке
 * поэтому НЕ означает «не держит» — оно означает «не в верхушке», и вызывающий
 * обязан сказать это словами.
 */
import type {
  ParticipantRow,
  ParticipantsStats,
  Token,
  TokenHolders,
} from "../api/types";
import { div, fixed, mul, parse, roundToFixed, type Micro } from "./money";
import { outcomeMap } from "./participants";
import { formatDollars, formatShares } from "./tape";

/** Одна строка держателя. */
export interface HolderRow {
  wallet: string;
  /** Имя профиля, иначе псевдоним, иначе пусто — тогда вызывающий покажет адрес. */
  name: string;
  /** Баланс в share. */
  amount: string;
  /**
   * Доля этого баланса от суммы ПОКАЗАННЫХ строк группы. Пусто — сумма нулевая.
   *
   * От показанных, а не от выпуска токена: выпуск нам неизвестен, а верхушка
   * списка — то, что на экране. Подпись обязана называть знаменатель.
   */
  share: string;
  /**
   * Средняя цена ПОКУПОК этого токена в этом окне. Пусто — кошелёк здесь не
   * покупал (минтил, принёс позицию или его нет среди показанных строк).
   */
  avg: string;
  /** Баланс по последней цене принта, в долларах. Пусто — цены принта нет. */
  value: string;
  /**
   * Что кошелёк набрал СДЕЛКАМИ по этому токену, со знаком.
   *
   * null означает «не знаем»: строки кошелька нет среди показанных — он не попал
   * в сотню крупнейших либо отсечён фильтром по пометкам. Это НЕ ноль: ноль
   * сказал бы «не торговал», а из этого следовал бы минт.
   */
  traded: string | null;
  /**
   * Баланс заметно больше набранного сделками — шеры пришли МИНТОМ.
   *
   * Считается только при полной выборке: на обрезанной накопление занижено, и
   * минт объявился бы у каждого второго.
   */
  minted: boolean;
  /** Наш кошелёк. */
  ours: boolean;
  /** Ручные пометки — те же, что в таблице накопления. */
  tags: string[];
}

/** Держатели одного исхода. Группировка сохраняется от источника. */
export interface HolderGroup {
  assetID: string;
  /** Имя исхода. Пусто — справочник рынка не загружен. */
  outcome: string;
  rows: HolderRow[];
  /** Σ балансов показанных строк, share. */
  total: string;
  /** Та же сумма по последней цене принта. Пусто — цены нет. */
  totalValue: string;
  /** Сколько держателей ПРИСЛАЛ источник по этому токену. */
  count: number;
  /**
   * Выдача источника насытилась: список — верхушка, а не состав. Судится по
   * `rows` группы, то есть по числу присланных строк, а не разобранных.
   */
  saturated: boolean;
  /** Строки оставлены отбором по отмеченным кошелькам. */
  onlyPicked: boolean;
}

/** Потолок выдачи держателей на токен — тот же, что просит сервер. */
const HOLDERS_LIMIT = 200;

/**
 * Порог, с которого расхождение баланса и накопления называется минтом.
 *
 * Два условия сразу, и оба нужны. Абсолютный отсекает пыль округления и
 * дробление лота; относительный — крупный кошелёк, у которого пара шеров разницы
 * не значит ничего. Числа выбраны так, чтобы минт не объявлялся у того, кто
 * просто купил: минтят парой, то есть сотнями шеров сразу.
 */
const MINT_MIN_SHARES = 1_000_000; // 1.0 share в микроединицах
const MINT_MIN_SHARE_FRACTION = 0.02;

/**
 * Держатели по группам исходов.
 *
 * `only` — отбор по отмеченным галочками кошелькам (адреса в нижнем регистре).
 * Пусто означает «все»: это ровно тот же список, который показывает свёрнутый
 * блок внизу панели, и второй функции ради подпункта «Итого» не заводится —
 * иначе два места считали бы одну и ту же среднюю цену.
 */
export function holderGroups(
  p: ParticipantsStats | undefined,
  tokens: readonly Token[] | undefined,
  only?: ReadonlySet<string>,
): HolderGroup[] {
  if (!p?.known || !p.holders) return [];
  const outcomes = outcomeMap(tokens);
  const byWallet = rowsByWallet(p.rows);
  const lastPrice = p.last_price ?? {};

  return p.holders.map((g: TokenHolders) =>
    groupOf(g, {
      outcome: outcomes.get(g.asset_id) ?? "",
      price: parse(lastPrice[g.asset_id]),
      byWallet,
      truncated: p.truncated,
      ...(only ? { only } : {}),
    }),
  );
}

function groupOf(
  g: TokenHolders,
  ctx: {
    outcome: string;
    price: Micro | null;
    byWallet: Map<string, ParticipantRow>;
    truncated: boolean;
    only?: ReadonlySet<string>;
  },
): HolderGroup {
  const kept = g.holders.filter(
    (h) => !ctx.only || ctx.only.has(h.wallet.toLowerCase()),
  );
  // Источник и так сортирует по балансу, но полагаться на это нельзя: порядок
  // строк — это порядок на экране, и чужая смена сортировки была бы видна как
  // случайно перемешанный список.
  const sorted = [...kept].sort(
    (a, b) => (parse(b.amount) ?? 0) - (parse(a.amount) ?? 0),
  );
  const total = sorted.reduce((sum, h) => sum + (parse(h.amount) ?? 0), 0);

  const rows = sorted.map((h) => {
    const amount = parse(h.amount) ?? 0;
    const row = ctx.byWallet.get(h.wallet.toLowerCase());
    const token = row?.tokens.find((t) => t.asset_id === g.asset_id);
    const net = token ? parse(token.net) : null;
    const bought = token ? parse(token.bought) : null;
    const boughtUSD = token ? parse(token.bought_usd) : null;
    const avg =
      bought !== null && boughtUSD !== null && bought > 0
        ? div(boughtUSD, bought)
        : null;
    const value = ctx.price === null ? null : mul(amount, ctx.price);

    return {
      wallet: h.wallet,
      name: h.name || h.pseudonym || "",
      amount: formatShares(h.amount),
      share:
        total > 0 ? `${Math.round((amount / total) * 100)} %` : "",
      avg: avg === null ? "" : roundToFixed(avg, 3),
      value: value === null ? "" : formatDollars(fixed(value)),
      traded: net === null ? null : formatShares(fixed(net)),
      minted: isMinted(amount, net, ctx.truncated),
      ours: Boolean(row?.ours),
      tags: row?.tags ?? [],
    };
  });

  return {
    assetID: g.asset_id,
    outcome: ctx.outcome,
    rows,
    total: formatShares(fixed(total)),
    totalValue:
      ctx.price === null
        ? ""
        : formatDollars(fixed(mul(total, ctx.price) ?? 0)),
    count: g.rows ?? g.holders.length,
    saturated: (g.rows ?? g.holders.length) >= HOLDERS_LIMIT,
    onlyPicked: Boolean(ctx.only),
  };
}

/**
 * Баланс пришёл минтом, а не покупкой.
 *
 * Незнание накопления (кошелька нет среди показанных строк) минтом НЕ считается:
 * это разные ответы, и объявить минт по отсутствию строки значило бы приписать
 * кошельку действие за то, что он не попал в верхушку таблицы.
 */
function isMinted(
  amount: Micro,
  net: Micro | null,
  truncated: boolean,
): boolean {
  if (truncated || net === null) return false;
  const gap = amount - net;
  return gap > MINT_MIN_SHARES && gap > amount * MINT_MIN_SHARE_FRACTION;
}

/** Показанные строки накопления по адресу в нижнем регистре. */
function rowsByWallet(rows: ParticipantRow[]): Map<string, ParticipantRow> {
  const out = new Map<string, ParticipantRow>();
  for (const row of rows) out.set(row.wallet.toLowerCase(), row);
  return out;
}
